/**
 * Custom Content Display Component
 * Displays custom product content including ingredients, instructions, and rich text messages
 */

import React from 'react';
import { CustomProductContent, ManufacturingDetails, ProduceDetails } from '../services/qr.service';
import { AlertTriangle, Calendar, MapPin, Package, Clock, Info } from 'lucide-react';

interface CustomContentDisplayProps {
  customContent?: CustomProductContent;
  manufacturingDetails?: ManufacturingDetails;
  produceDetails?: ProduceDetails;
  productType?: 'manufactured' | 'produce';
  className?: string;
}

const CustomContentDisplay: React.FC<CustomContentDisplayProps> = ({
  customContent,
  manufacturingDetails,
  produceDetails,
  productType = 'manufactured',
  className = ''
}) => {
  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatMrp = (value: number): string => {
    if (!Number.isFinite(value)) {
      return String(value);
    }
    return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  const renderRichText = (text: string): JSX.Element => {
    // Simple rich text rendering - converts basic markdown-like formatting
    let formattedText = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
      .replace(/\n/g, '<br />'); // Line breaks

    return (
      <div 
        dangerouslySetInnerHTML={{ __html: formattedText }}
        className="prose prose-sm max-w-none"
      />
    );
  };

  if (!customContent && !manufacturingDetails && !produceDetails) {
    return null;
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Produce Details */}
      {produceDetails && productType === 'produce' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Harvest Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {produceDetails.harvestDate && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-green-600" />
                <span className="font-medium">Harvested:</span>
                <span>{formatDate(produceDetails.harvestDate)}</span>
              </div>
            )}
            {produceDetails.origin && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-green-600" />
                <span className="font-medium">Origin:</span>
                <span>{produceDetails.origin}</span>
              </div>
            )}
            {produceDetails.variety && (
              <div className="flex items-center gap-2 text-sm">
                <Package className="w-4 h-4 text-green-600" />
                <span className="font-medium">Variety:</span>
                <span>{produceDetails.variety}</span>
              </div>
            )}
            {produceDetails.storageInstructions && (
              <div className="flex items-center gap-2 text-sm">
                <Info className="w-4 h-4 text-green-600" />
                <span className="font-medium">Storage:</span>
                <span>{produceDetails.storageInstructions}</span>
              </div>
            )}
            {produceDetails.expiryDate && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-green-600" />
                <span className="font-medium">Best before:</span>
                <span>{formatDate(produceDetails.expiryDate)}</span>
              </div>
            )}
            {produceDetails.bestBeforeDays && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-green-600" />
                <span className="font-medium">Shelf life:</span>
                <span>{produceDetails.bestBeforeDays} day{produceDetails.bestBeforeDays === 1 ? '' : 's'}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manufacturing Details (hide for produce) */}
      {manufacturingDetails && productType !== 'produce' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Manufacturing Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {manufacturingDetails.manufacturingDate && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="font-medium">Manufactured:</span>
                <span>{formatDate(manufacturingDetails.manufacturingDate)}</span>
              </div>
            )}
            {manufacturingDetails.expiryDate && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="font-medium">Expires:</span>
                <span>{formatDate(manufacturingDetails.expiryDate)}</span>
              </div>
            )}
            {manufacturingDetails.batchNumber && (
              <div className="flex items-center gap-2 text-sm">
                <Package className="w-4 h-4 text-blue-600" />
                <span className="font-medium">Batch Number:</span>
                <span className="font-mono">{manufacturingDetails.batchNumber}</span>
              </div>
            )}
            {manufacturingDetails.mrp !== undefined && manufacturingDetails.mrp !== null && (
              <div className="flex items-center gap-2 text-sm">
                <Info className="w-4 h-4 text-blue-600" />
                <span className="font-medium">MRP (INR):</span>
                <span>{formatMrp(manufacturingDetails.mrp)}</span>
              </div>
            )}
            {manufacturingDetails.facilityLocation && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-blue-600" />
                <span className="font-medium">Facility:</span>
                <span>{manufacturingDetails.facilityLocation}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ingredients */}
      {customContent?.ingredients && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Ingredients</h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            {customContent.ingredients}
          </p>
        </div>
      )}

      {/* Allergens */}
      {customContent?.allergens && customContent.allergens.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Allergen Information
          </h3>
          <div className="flex flex-wrap gap-2">
            {customContent.allergens.map((allergen, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800"
              >
                {allergen}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Usage Instructions */}
      {customContent?.usageInstructions && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
            <Info className="w-5 h-5" />
            Usage Instructions
          </h3>
          <div className="text-sm text-green-800">
            {renderRichText(customContent.usageInstructions)}
          </div>
        </div>
      )}

      {/* Storage Instructions */}
      {customContent?.storageInstructions && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Storage Instructions
          </h3>
          <div className="text-sm text-yellow-800">
            {renderRichText(customContent.storageInstructions)}
          </div>
        </div>
      )}

      {/* Custom Message */}
      {customContent?.customMessage && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-purple-900 mb-3">
            {productType === 'produce' ? 'Message from Grower' : 'Message from Manufacturer'}
          </h3>
          <div className="text-sm text-purple-800">
            {renderRichText(customContent.customMessage)}
          </div>
        </div>
      )}

      {/* Brand Story */}
      {customContent?.brandStory && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-indigo-900 mb-3">Our Story</h3>
          <div className="text-sm text-indigo-800">
            {renderRichText(customContent.brandStory)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomContentDisplay;
